import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Info } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface TimelineSectionProps {
  timelineMonths: number;
  rampPeriodMonths: number;
  onChange: (field: "timelineMonths" | "rampPeriodMonths") => (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export const TimelineSection = ({
  timelineMonths,
  rampPeriodMonths,
  onChange,
}: TimelineSectionProps) => {
  const [tooltipOpen, setTooltipOpen] = React.useState(false);

  const handleInputChange = (field: "timelineMonths" | "rampPeriodMonths") => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow any decimal input including empty string
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      const numericValue = Number(value);
      if (!isNaN(numericValue)) {
        onChange(field)(e);
      }
    }
  };

  // Format value for display - return empty string for 0 only in display
  const formatDisplayValue = (value: number) => 
    value === 0 && !String(value).length ? '' : String(value);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="space-y-2">
        <div className="flex items-center gap-2 h-[24px]">
          <Label htmlFor="timelineMonths">Timeline (Months)</Label>
        </div>
        <Input
          id="timelineMonths"
          type="number"
          step="0.1"
          min="0"
          value={formatDisplayValue(timelineMonths)}
          onChange={handleInputChange("timelineMonths")}
          required
        />
      </div>
      <div className="space-y-2">
        <div className="flex items-center gap-2 h-[24px]">
          <Label htmlFor="rampPeriodMonths">Ramp Period (Months)</Label>
          <TooltipProvider>
            <Tooltip open={tooltipOpen} onOpenChange={setTooltipOpen}>
              <TooltipTrigger asChild>
                <button 
                  type="button" 
                  className="cursor-help"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setTooltipOpen(!tooltipOpen);
                  }}
                >
                  <Info className="h-4 w-4 text-muted-foreground" />
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Applies to new recruiters only</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <Input
          id="rampPeriodMonths"
          type="number"
          step="0.1"
          min="0"
          value={formatDisplayValue(rampPeriodMonths)}
          onChange={handleInputChange("rampPeriodMonths")}
          required
        />
      </div>
    </div>
  );
};